/**
 * AgentJobsPanel — Job results panel for the AI Agent.
 *
 * Changes vs previous version:
 *   • Grid now shows up to 3 columns on wider screens (was max 2)
 *   • Pagination (PAGE_SIZE=24) — fills viewport, reduces empty space
 *   • Collections: bookmark jobs into named collections, persisted to localStorage
 *   • "Add to collection" dropdown per card + collection-filter chip in header
 */

import {
  BarChart2,
  Bookmark,
  BookmarkCheck,
  Briefcase,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Database,
  DollarSign,
  FolderOpen,
  Globe,
  GraduationCap,
  Grid2X2,
  KeyRound,
  List,
  MapPin,
  MoreHorizontal,
  PanelRightClose,
  Plus,
  Star,
  StarOff,
  X,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import CompanyLogoShared from '../../../components/CompanyLogo';
import LoadingSpinner from '../../../components/LoadingSpinner';

import JobCard, { type Job } from '../../dashboard/components/JobCard';
import { useJobContentTranslation } from '../../dashboard/hooks/useJobContentTranslation';
import JobDetailDrawer from '../../dashboard/components/Jobs/JobDetailDrawer';
import JobWorkflowStatusBadge from './JobWorkflowStatusBadge';
import { useBlindModeValue } from '../../dashboard/hooks/useBlindMode';
import type { RecommendationResult } from '../../recommendations/types';
import { useAppliedJobsMap } from '../hooks/useAppliedJobsMap';
import type { AppliedJobInfo } from '../hooks/useAppliedJobsMap';

// ── Constants ────────────────────────────────────────────────────────
const PAGE_SIZE = 24;

// ── Blind-mode company label helpers (shares sessionStorage with useAnonymizedJob) ──
const _blindMap: Map<string, string> = (() => {
  try {
    const raw = sessionStorage.getItem('ggj:blind-company-map');
    return raw ? new Map(JSON.parse(raw) as [string, string][]) : new Map();
  } catch { return new Map(); }
})();

function _blindLabel(jobId: string): string {
  if (_blindMap.has(jobId)) return _blindMap.get(jobId)!;
  const i = _blindMap.size;
  const a = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const label = i < 26 ? `Company ${a[i]}` : `Company ${a[Math.floor(i / 26) - 1]}${a[i % 26]}`;
  _blindMap.set(jobId, label);
  try { sessionStorage.setItem('ggj:blind-company-map', JSON.stringify([..._blindMap.entries()])); } catch { /* noop */ }
  return label;
}
const COLLECTIONS_KEY = 'ggj_job_collections';

// ── Types ────────────────────────────────────────────────────────────
export interface Collection {
  id:        string;
  name:      string;
  jobIds:    string[];
  createdAt: string;
}

// ── Band colour helpers ──────────────────────────────────────────────
const BAND_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  strong_fit:      { bg: 'rgba(16,185,129,0.12)',  text: '#10b981', label: 'Strong fit'  },
  good_fit:        { bg: 'rgba(59,130,246,0.12)',  text: '#3b82f6', label: 'Good fit'    },
  partial_fit:     { bg: 'rgba(245,158,11,0.12)',  text: '#f59e0b', label: 'Partial fit' },
  not_recommended: { bg: 'rgba(239,68,68,0.10)',   text: '#ef4444', label: 'Low match'   },
};

export interface AgentJob {
  id:                  string;
  title:               string;
  company:             string;
  company_logo_url?:   string | null;
  location?:           string | null;
  remote?:             boolean | null;
  job_type?:           string | null;
  experience_level?:   string | null;
  skills?:             string[] | null;
  must_have_skills?:   string[] | null;
  good_to_have_skills?: string[] | null;
  url?:                string | null;
  salary_min?:         number | null;
  salary_max?:         number | null;
  /** 'peerlist' | 'linkedin' | 'naukri' — used for source badge in card */
  source_channel?:     string | null;
  salary_currency?:    string | null;
  compensation_text?:  string | null;
  posted_at?:          string | null;
  description_md?:     string | null;
}

interface AgentJobsPanelProps {
  query:            string;
  jobs:             AgentJob[];
  recommendations:  Map<string, RecommendationResult>;
  isLoading:        boolean;
  onClose:          () => void;
  searchSource?:    'internal' | 'external';
  deepSearch?:      boolean;
  onAgentOptimize?: (job: AgentJob) => void;
  /** Controlled collections from parent (AIAgentPanel) */
  collections:           Collection[];
  activeCollectionId:    string | null;
  onCollectionsChange:   (c: Collection[]) => void;
  onCollectionSelect:    (id: string | null) => void;
  /** Lifted state — controlled by AIAgentPanel toolbar */
  viewMode:           'grid' | 'list';
  onViewModeChange:   (m: 'grid' | 'list') => void;
  shortlisted:        Set<string>;
  onShortlistedChange:(ids: Set<string>) => void;
  /** Reports filtered count back so the parent toolbar can show it */
  onFilteredCount?:   (n: number) => void;
  /** Called when the user opens a job in the detail drawer — lets parent track the focused job id. */
  onJobOpen?:         (jobId: string) => void;
  /** When true (chat panel visible), use 2-col grid; when false (full screen), use 3-4 cols */
  chatVisible?:       boolean;
  /** Hide jobs panel (keep results in memory) — expand via Agent header */
  onCollapse?:        () => void;
}

/** Plain-text snippet for JobCard body (matches Jobs tab cards). */
function stripJobDescription(raw?: string | null): string | undefined {
  if (!raw?.trim()) return undefined;
  return raw
    .replace(/<[^>]+>/g, ' ')
    .replace(/[#*_`>[\]|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 320);
}

/** Convert AgentJob → Job (JobCard/Drawer shape). */
function toJob(a: AgentJob, rec?: RecommendationResult): Job {
  // Infer source_channel from URL if not explicitly set
  let channel = a.source_channel?.toLowerCase() ?? null;
  if (!channel && a.url) {
    if (a.url.includes('naukri.com')) channel = 'naukri';
    else if (a.url.includes('linkedin.com')) channel = 'linkedin';
    else if (a.url.includes('peerlist.io')) channel = 'peerlist';
  }
  const isPeerlist = channel === 'peerlist';
  return {
    id:                  a.id,
    source:              isPeerlist ? 'peerlist' : 'direct',
    source_channel:      channel ?? undefined,
    title:               a.title,
    company:             a.company,
    company_logo_url:    a.company_logo_url ?? undefined,
    location:            a.location ?? '',
    url:                 a.url ?? '#',
    created:             a.posted_at ?? new Date().toISOString(),
    description:         stripJobDescription(a.description_md ?? a.description),
    skills:              a.skills ?? undefined,
    must_have_skills:    a.must_have_skills ?? undefined,
    good_to_have_skills: a.good_to_have_skills ?? undefined,
    salary_min:          a.salary_min ?? undefined,
    salary_max:          a.salary_max ?? undefined,
    salary_currency:     a.salary_currency ?? 'INR',
    job_type:            a.job_type ?? undefined,
    experience_level:    a.experience_level ?? undefined,
    remote:              a.remote ?? undefined,
    compensation_text:   a.compensation_text ?? undefined,
    posted_at:           a.posted_at ?? undefined,
    matchScore:          rec ? rec.score : undefined,
    matchedSkills:       rec?.matched_skills,
    missingSkills:       rec?.missing_skills,
  };
}

// ── Freshness helper ────────────────────────────────────────────────
function agentFreshnessLabel(iso?: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const h = diff / 3600000;
  if (h < 1)  return 'Just now';
  if (h < 24) return `${Math.floor(h)}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'Yesterday';
  if (d < 7)  return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// ── Salary formatter ─────────────────────────────────────────────────
function agentSalaryLabel(min?: number | null, max?: number | null, currency?: string | null, text?: string | null): string {
  if (!min && !max) return text ?? '';
  const fmt = (v: number) => {
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency ?? 'INR', maximumFractionDigits: 0, notation: 'compact' }).format(v);
    } catch { return `${currency ?? '₹'}${v.toLocaleString()}`; }
  };
  if (min && max) return `${fmt(min)} – ${fmt(max)}`;
  if (min) return `From ${fmt(min)}`;
  if (max) return `Up to ${fmt(max)}`;
  return '';
}

// ── Company logo with favicon fallback ───────────────────────────────
const CompanyLogoSmall: React.FC<{ url?: string | null; company: string }> = ({ url, company }) => (
  <CompanyLogoShared
    url={url}
    company={company}
    imgClassName="w-8 h-8 rounded-lg object-contain border shrink-0 p-0.5"
    imgStyle={{ background: '#fff', borderColor: 'rgba(0,0,0,0.06)' }}
    fallbackClassName="w-8 h-8 rounded-lg flex items-center justify-center font-bold shrink-0"
    fallbackStyle={{ background: 'var(--ds-brand-soft)', color: 'var(--ds-brand)' }}
    alt={company}
  />
);

// ── Source badge ─────────────────────────────────────────────────────
const SourceBadge: React.FC<{ channel?: string | null }> = ({ channel }) => {
  if (!channel || channel === 'peerlist') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold whitespace-nowrap"
        style={{ background: 'rgba(0,170,69,0.10)', color: '#00AA45' }}>
        <svg viewBox="0 0 24 24" width="8" height="8" fill="currentColor"><circle cx="12" cy="12" r="10" /></svg>
        Peerlist
      </span>
    );
  }
  if (channel === 'linkedin') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold whitespace-nowrap"
        style={{ background: 'rgba(10,102,194,0.10)', color: '#0A66C2' }}>
        <svg viewBox="0 0 24 24" width="8" height="8" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
        LinkedIn
      </span>
    );
  }
  if (channel === 'naukri') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold whitespace-nowrap"
        style={{ background: 'rgba(217,119,6,0.10)', color: '#d97706' }}>
        Naukri
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full t-tag font-semibold whitespace-nowrap"
      style={{ background: 'var(--ds-bg-tinted)', color: 'var(--ds-text-muted)' }}>
      {channel}
    </span>
  );
};

// ── Score badge ──────────────────────────────────────────────────────
const ScoreBadge: React.FC<{ rec: RecommendationResult }> = ({ rec }) => {
  const style = BAND_STYLE[rec.band] ?? BAND_STYLE.partial_fit;
  return (
    <div
      className="flex items-center gap-1 px-2 py-0.5 rounded-full t-tag font-semibold shrink-0"
      style={{ background: style.bg, color: style.text }}
      title={`${style.label} — ${rec.score.toFixed(0)}% match`}
    >
      <BarChart2 className="w-3 h-3" />
      {rec.score.toFixed(0)}%
    </div>
  );
};

// ── FilterChip ───────────────────────────────────────────────────────
const FilterChip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active, onClick, children,
}) => (
  <button type="button" onClick={onClick}
    className="shrink-0 inline-flex items-center gap-1 rounded-full border text-[11px] font-medium px-2 py-0.5 transition-colors touch-manipulation whitespace-nowrap"
    style={active
      ? { borderColor: 'var(--ds-brand)', color: 'var(--ds-brand)', background: 'var(--ds-brand-soft)' }
      : { borderColor: 'var(--ds-border)', color: 'var(--ds-text-muted)', background: 'transparent' }
    }
    aria-selected={active}>
    {children}
  </button>
);

// ── "Add to Collection" dropdown per card ────────────────────────────
interface CollectionDropdownProps {
  jobId:       string;
  collections: Collection[];
  onAdd:       (collectionId: string) => void;
  onCreate:    (name: string) => void;
  onClose:     () => void;
}
const CollectionDropdown: React.FC<CollectionDropdownProps> = ({
  jobId, collections, onAdd, onCreate, onClose,
}) => {
  const [newName, setNewName] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="ds-card-pop absolute right-0 top-full mt-1 z-50 py-1 w-[200px]"
    >
      <p className="px-3 py-1.5 t-label">
        Save to collection
      </p>

      {collections.length === 0 && (
        <p className="px-3 py-2 t-tag t-muted">
          No collections yet.
        </p>
      )}

      {collections.map((c) => {
        const alreadyIn = c.jobIds.includes(jobId);
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => { onAdd(c.id); onClose(); }}
            className="w-full flex items-center gap-2 px-3 py-2 t-meta transition-colors hover:bg-[var(--ds-bg-tinted)] text-left"
            style={{ color: alreadyIn ? 'var(--ds-brand)' : 'var(--ds-text-strong)' }}
          >
            <FolderOpen className="w-3.5 h-3.5 shrink-0" />
            <span className="flex-1 truncate">{c.name}</span>
            {alreadyIn && <BookmarkCheck className="w-3 h-3 shrink-0" style={{ color: 'var(--ds-brand)' }} />}
          </button>
        );
      })}

      <div className="border-t mx-2 my-1" style={{ borderColor: 'var(--ds-border)' }} />

      {/* Create new */}
      <div className="px-2 py-1 flex gap-1">
        <input
          autoFocus
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newName.trim()) {
              onCreate(newName.trim());
              onClose();
            }
          }}
          placeholder="New collection…"
          className="ds-input flex-1 min-w-0"
        />
        <button
          type="button"
          onClick={() => { if (newName.trim()) { onCreate(newName.trim()); onClose(); } }}
          disabled={!newName.trim()}
          className="ds-btn ds-btn-primary w-7 h-7 p-0 disabled:opacity-30"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────
// ── Applied / Optimized badge — see JobWorkflowStatusBadge.tsx ───────────────

const AgentJobsPanel: React.FC<AgentJobsPanelProps> = ({
  query,
  jobs,
  recommendations,
  isLoading,
  onClose,
  searchSource = 'internal',
  deepSearch = false,
  onAgentOptimize,
  collections,
  activeCollectionId,
  onCollectionsChange,
  onCollectionSelect,
  viewMode,
  onViewModeChange,
  shortlisted,
  onShortlistedChange,
  onFilteredCount,
  onJobOpen,
  onCollapse,
}) => {
  const { t, i18n } = useTranslation();
  // ── Blind mode — anonymises company names when ON ────────────────────────
  const blindMode = useBlindModeValue();

  // ── Applied-jobs lookup — surfaces "Applied" / "Optimized Earlier" badge ──
  const { lookup: lookupApplied } = useAppliedJobsMap();

  const [selectedJob,       setSelectedJob]       = useState<Job | null>(null);
  const [savedLocal,        setSavedLocal]        = useState<Set<string>>(new Set());
  const [selectedJobIds,    setSelectedJobIds]    = useState<Set<string>>(new Set());
  const [bulkCollDropOpen,  setBulkCollDropOpen]  = useState(false);
  const bulkCollDropRef = useRef<HTMLDivElement>(null);
  const [currentPage,       setCurrentPage]       = useState(1);
  const [openCollDropId,    setOpenCollDropId]    = useState<string | null>(null);

  // Active filter values
  const [remoteOnly,      setRemoteOnly]      = useState(false);
  const [jobTypeFilter,   setJobTypeFilter]   = useState<string>('');
  const [expFilter,       setExpFilter]       = useState<string>('');
  const [sourceFilter,    setSourceFilter]    = useState<string>('');
  const [locationFilter,  setLocationFilter]  = useState<string>('');
  const [shortlistOnly,   setShortlistOnly]   = useState(false);
  const [minScoreFilter,  setMinScoreFilter]  = useState<number>(0);
  const [postedDaysFilter, setPostedDaysFilter] = useState<number>(0);
  const [sortMode,        setSortMode]        = useState<'best' | 'match' | 'recent' | 'salary'>('recent');

  // Reset to page 1 whenever filters/jobs change
  useEffect(() => { setCurrentPage(1); }, [jobs, remoteOnly, jobTypeFilter, expFilter, sourceFilter, locationFilter, shortlistOnly, minScoreFilter, postedDaysFilter, sortMode, activeCollectionId]);

  const convertedJobs = useMemo(
    () => jobs.map((j) => toJob(j, recommendations.get(j.id))),
    [jobs, recommendations],
  );

  const jobTypes = useMemo(
    () => [...new Set(convertedJobs.map((j) => j.job_type).filter(Boolean) as string[])].sort(),
    [convertedJobs],
  );
  const expLevels = useMemo(
    () => [...new Set(convertedJobs.map((j) => j.experience_level).filter(Boolean) as string[])].sort(),
    [convertedJobs],
  );
  const sourceChannels = useMemo(() => {
    const channels = convertedJobs
      .map((j) => (j as Job & { source_channel?: string }).source_channel ?? (j.source === 'peerlist' ? 'peerlist' : null))
      .filter(Boolean) as string[];
    return [...new Set(channels)].sort();
  }, [convertedJobs]);
  const locations = useMemo(() => {
    const locs = convertedJobs
      .map((j) => {
        const raw = j.location?.trim();
        if (!raw) return null;
        return raw.split(',')[0]?.trim() || null;
      })
      .filter(Boolean) as string[];
    const counts = new Map<string, number>();
    for (const l of locs) counts.set(l, (counts.get(l) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([l]) => l).slice(0, 15);
  }, [convertedJobs]);

  const activeCollJobIds = useMemo(
    () => activeCollectionId ? new Set(collections.find((c) => c.id === activeCollectionId)?.jobIds ?? []) : null,
    [activeCollectionId, collections],
  );

  const filtered = useMemo(() => {
    const getPostedTs = (j: Job) => {
      const t = j.posted_at || j.created;
      return t ? new Date(t).getTime() : 0;
    };

    const list = convertedJobs.filter((j) => {
      if (remoteOnly             && !j.remote) return false;
      if (jobTypeFilter          && j.job_type !== jobTypeFilter) return false;
      if (expFilter              && j.experience_level !== expFilter) return false;
      if (sourceFilter) {
        const ch = (j as Job & { source_channel?: string }).source_channel ?? (j.source === 'peerlist' ? 'peerlist' : '');
        if (ch !== sourceFilter) return false;
      }
      if (locationFilter && !(j.location ?? '').toLowerCase().includes(locationFilter.toLowerCase())) return false;
      if (shortlistOnly          && !shortlisted.has(j.id)) return false;
      if (activeCollJobIds       && !activeCollJobIds.has(j.id)) return false;
      if (minScoreFilter > 0) {
        const score = j.matchScore ?? recommendations.get(j.id)?.score ?? 0;
        if (score < minScoreFilter) return false;
      }
      if (postedDaysFilter > 0) {
        const ts = getPostedTs(j);
        if (ts > 0 && ts < Date.now() - postedDaysFilter * 24 * 3600 * 1000) return false;
      }
      return true;
    });

    const getScore = (j: Job) => j.matchScore ?? recommendations.get(j.id)?.score ?? 0;
    const getSalary = (j: Job) => Math.max(j.salary_max ?? 0, j.salary_min ?? 0);

    if (sortMode === 'best') {
      // Combined score: 60% match relevance + 40% recency (normalized to 0-100)
      const now = Date.now();
      const maxAge = 30 * 24 * 3600 * 1000; // 30 days
      list.sort((a, b) => {
        const scoreA = getScore(a);
        const scoreB = getScore(b);
        const ageA = Math.max(0, 1 - (now - getPostedTs(a)) / maxAge);
        const ageB = Math.max(0, 1 - (now - getPostedTs(b)) / maxAge);
        const combinedA = scoreA * 0.6 + ageA * 100 * 0.4;
        const combinedB = scoreB * 0.6 + ageB * 100 * 0.4;
        return combinedB - combinedA;
      });
    } else if (sortMode === 'match') {
      list.sort((a, b) => getScore(b) - getScore(a));
    } else if (sortMode === 'recent') {
      list.sort((a, b) => getPostedTs(b) - getPostedTs(a));
    } else if (sortMode === 'salary') {
      list.sort((a, b) => getSalary(b) - getSalary(a));
    }

    return list;
  }, [convertedJobs, remoteOnly, jobTypeFilter, expFilter, sourceFilter, locationFilter, shortlistOnly, minScoreFilter, postedDaysFilter, sortMode, shortlisted, activeCollJobIds, recommendations]);

  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated   = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const activeFilterCount =
    (remoteOnly ? 1 : 0) + (jobTypeFilter ? 1 : 0) + (expFilter ? 1 : 0) + (sourceFilter ? 1 : 0) + (locationFilter ? 1 : 0) + (shortlistOnly ? 1 : 0) + (minScoreFilter > 0 ? 1 : 0) + (postedDaysFilter > 0 ? 1 : 0) + (activeCollectionId ? 1 : 0);

  const hasRecs = recommendations.size > 0;

  // Report filtered count upward so toolbar can display it
  useEffect(() => { onFilteredCount?.(filtered.length); }, [filtered.length, onFilteredCount]);

  const toggleShortlist = useCallback((job: Job) => {
    const next = new Set(shortlisted);
    if (next.has(job.id)) next.delete(job.id); else next.add(job.id);
    onShortlistedChange(next);
  }, [shortlisted, onShortlistedChange]);

  const toggleSave = useCallback((job: Job) => {
    setSavedLocal((prev) => {
      const next = new Set(prev);
      if (next.has(job.id)) next.delete(job.id); else next.add(job.id);
      return next;
    });
  }, []);

  const clearFilters = () => {
    setRemoteOnly(false); setJobTypeFilter(''); setExpFilter(''); setSourceFilter('');
    setLocationFilter(''); setShortlistOnly(false); setMinScoreFilter(0); setPostedDaysFilter(0);
    setSortMode('recent');
    onCollectionSelect(null);
  };

  const toggleSelect = useCallback((jobId: string) => {
    setSelectedJobIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });
  }, []);

  const clearSelection = () => setSelectedJobIds(new Set());

  // Close bulk coll dropdown on outside click
  useEffect(() => {
    if (!bulkCollDropOpen) return;
    const h = (e: MouseEvent) => {
      if (bulkCollDropRef.current && !bulkCollDropRef.current.contains(e.target as Node)) {
        setBulkCollDropOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [bulkCollDropOpen]);

  // ── Collections helpers ──
  const handleAddToCollection = useCallback((collId: string, jobId: string) => {
    onCollectionsChange(
      collections.map((c) =>
        c.id === collId
          ? { ...c, jobIds: c.jobIds.includes(jobId) ? c.jobIds.filter((id) => id !== jobId) : [...c.jobIds, jobId] }
          : c,
      ),
    );
  }, [collections, onCollectionsChange]);

  const handleCreateCollection = useCallback((name: string, jobId: string) => {
    const newColl: Collection = {
      id:        `coll_${Date.now()}`,
      name,
      jobIds:    [jobId],
      createdAt: new Date().toISOString(),
    };
    onCollectionsChange([...collections, newColl]);
  }, [collections, onCollectionsChange]);

  const jobInAnyCollection = useCallback((jobId: string) =>
    collections.some((c) => c.jobIds.includes(jobId)),
  [collections]);

  const handleJobCardClick = useCallback((job: Job) => {
    setSelectedJob(job);
    onJobOpen?.(job.id);
  }, [onJobOpen]);

  const handleJobCardApply = useCallback((job: Job) => {
    const raw = jobs.find((j) => j.id === job.id);
    if (raw && onAgentOptimize) {
      onAgentOptimize(raw);
      return;
    }
    setSelectedJob(job);
  }, [jobs, onAgentOptimize]);

  return (
    <div className="ds-jobs-page ds-agent-jobs-panel relative flex flex-col h-full overflow-hidden">

      {/* ── Unified compact header — all controls in one tight row ── */}
      <div className="agent-jobs-mobile-toolbar shrink-0 border-b px-2 py-1 flex items-center gap-1.5 sm:gap-2 sm:px-3 sm:py-1.5"
        style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)' }}>

        {/* Left: Title + count badge */}
        <div className="flex items-center gap-1.5 min-w-0 shrink-0">
          <span className="t-card-title">
            {isLoading ? t('agent.jobsPanel.searching', 'Searching…') : t('agent.jobsPanel.title', 'Jobs')}
          </span>
          {!isLoading && (
            <span className="ds-chip ds-chip-brand t-tag font-semibold tabular-nums">
              {filtered.length}
            </span>
          )}
          {deepSearch && (
            <span className="hidden sm:inline-flex ds-chip ds-chip-brand t-tag">
              <Globe className="w-2 h-2" /> {t('agent.jobsPanel.deep', 'Deep')}
            </span>
          )}
        </div>

        <div className="flex-1" />

        {/* Sort */}
        <select
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value as 'best' | 'match' | 'recent' | 'salary')}
          className="ds-select ds-select-sm shrink-0 !w-[8.5rem] max-w-[8.5rem]"
          aria-label="Sort jobs"
          title="Sort jobs"
        >
          <option value="recent">{t('agent.jobsPanel.sortByDate', 'Sort by date')}</option>
          <option value="best">{t('agent.jobsPanel.sortBest', 'Best match')}</option>
          <option value="match">{t('agent.jobsPanel.sortRelevance', 'Relevance')}</option>
          <option value="salary">{t('agent.jobsPanel.sortSalary', 'Salary')}</option>
        </select>

        {/* Grid / List toggle — list hidden on mobile (table unreadable) */}
        <div className="hidden sm:flex items-center rounded-md overflow-hidden border shrink-0"
          style={{ borderColor: 'var(--ds-border)' }}>
          <button type="button" onClick={() => onViewModeChange('grid')} title="Grid view"
            className="flex items-center justify-center w-6 h-6 transition-colors"
            style={{ color: viewMode === 'grid' ? '#10b981' : 'var(--ds-text-muted)', background: viewMode === 'grid' ? 'rgba(16,185,129,0.12)' : 'transparent' }}>
            <Grid2X2 className="w-3 h-3" />
          </button>
          <button type="button" onClick={() => onViewModeChange('list')} title="List view"
            className="flex items-center justify-center w-6 h-6 transition-colors"
            style={{ color: viewMode === 'list' ? '#3b82f6' : 'var(--ds-text-muted)', background: viewMode === 'list' ? 'rgba(59,130,246,0.12)' : 'transparent' }}>
            <List className="w-3 h-3" />
          </button>
        </div>

        {/* Collapse jobs panel — icon only */}
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            title="Collapse Jobs panel"
            aria-label="Collapse Jobs panel"
            className="ds-agent-panel-toggle shrink-0"
            style={{ color: '#8b5cf6' }}
          >
            <PanelRightClose className="w-4 h-4" style={{ color: '#8b5cf6' }} />
          </button>
        )}

        {/* Close — dismiss results entirely */}
        <button type="button" onClick={onClose} title="Close results"
          className="ds-btn ds-btn-ghost w-6 h-6 p-0 shrink-0"
          style={{ color: '#ef4444' }}>
          <X className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
        </button>
      </div>

      {/* ── Always-visible filter chip bar ── */}
      {!isLoading && jobs.length > 0 && (
        <div className="shrink-0 border-b px-3 py-1.5 flex items-center gap-1.5 overflow-x-auto overscroll-x-contain"
          style={{ borderColor: 'var(--ds-border)', scrollbarWidth: 'none' }}>
          {/* Posted date filter */}
          <select
            value={postedDaysFilter}
            onChange={(e) => setPostedDaysFilter(Number(e.target.value))}
            className="ds-select ds-select-sm shrink-0 !text-[11px] !min-h-0 !h-6 !py-0 !px-2 !rounded-full"
            style={postedDaysFilter > 0 ? { borderColor: 'var(--ds-brand)', color: 'var(--ds-brand)', background: 'var(--ds-brand-soft)' } : {}}
            aria-label="Filter by posted date"
          >
            <option value={0}>{t('agent.jobsPanel.postedAny', 'Posted')}</option>
            <option value={7}>{t('agent.jobsPanel.posted7d', 'Last 7 days')}</option>
            <option value={30}>{t('agent.jobsPanel.posted30d', 'Last 30 days')}</option>
          </select>

          {/* Source channel filter — always show if data exists */}
          {sourceChannels.length > 0 && (
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
              className="ds-select ds-select-sm shrink-0 !text-[11px] !min-h-0 !h-6 !py-0 !px-2 !rounded-full"
              style={sourceFilter ? { borderColor: 'var(--ds-brand)', color: 'var(--ds-brand)', background: 'var(--ds-brand-soft)' } : {}}>
              <option value="">Source</option>
              {sourceChannels.map((ch) => <option key={ch} value={ch}>{ch.charAt(0).toUpperCase() + ch.slice(1)}</option>)}
            </select>
          )}

          {/* Experience level filter — always show if data exists */}
          {expLevels.length > 0 && (
            <select value={expFilter} onChange={(e) => setExpFilter(e.target.value)}
              className="ds-select ds-select-sm shrink-0 !text-[11px] !min-h-0 !h-6 !py-0 !px-2 !rounded-full"
              style={expFilter ? { borderColor: 'var(--ds-brand)', color: 'var(--ds-brand)', background: 'var(--ds-brand-soft)' } : {}}>
              <option value="">Level</option>
              {expLevels.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          )}

          {/* Location filter — always show if data exists */}
          {locations.length > 0 && (
            <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}
              className="ds-select ds-select-sm shrink-0 !text-[11px] !min-h-0 !h-6 !py-0 !px-2 !rounded-full"
              style={locationFilter ? { borderColor: 'var(--ds-brand)', color: 'var(--ds-brand)', background: 'var(--ds-brand-soft)' } : {}}>
              <option value="">Location</option>
              {locations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          )}

          {/* Job type filter — always show if data exists */}
          {jobTypes.length > 0 && (
            <select value={jobTypeFilter} onChange={(e) => setJobTypeFilter(e.target.value)}
              className="ds-select ds-select-sm shrink-0 !text-[11px] !min-h-0 !h-6 !py-0 !px-2 !rounded-full"
              style={jobTypeFilter ? { borderColor: 'var(--ds-brand)', color: 'var(--ds-brand)', background: 'var(--ds-brand-soft)' } : {}}>
              <option value="">Type</option>
              {jobTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}

          <FilterChip active={remoteOnly} onClick={() => setRemoteOnly((v) => !v)}>Remote</FilterChip>
          {shortlisted.size > 0 && (
            <FilterChip active={shortlistOnly} onClick={() => setShortlistOnly((v) => !v)}>
              <Star className="w-2.5 h-2.5" /> Starred
            </FilterChip>
          )}

          {/* Min match score */}
          {hasRecs && (
            <select value={minScoreFilter} onChange={(e) => setMinScoreFilter(Number(e.target.value))}
              className="ds-select ds-select-sm shrink-0 !text-[11px] !min-h-0 !h-6 !py-0 !px-2 !rounded-full"
              style={minScoreFilter > 0 ? { borderColor: 'var(--ds-brand)', color: 'var(--ds-brand)', background: 'var(--ds-brand-soft)' } : {}}>
              <option value={0}>Match %</option>
              <option value={50}>50%+</option>
              <option value={65}>65%+</option>
              <option value={80}>80%+</option>
            </select>
          )}

          {activeFilterCount > 0 && (
            <button type="button" onClick={clearFilters}
              className="shrink-0 ml-auto text-[11px] font-medium px-2 py-0.5 rounded-full transition-colors hover:opacity-80"
              style={{ color: 'var(--ds-danger)', background: 'rgba(239,68,68,0.08)' }}>
              Clear
            </button>
          )}
        </div>
      )}

      {/* Shortlisted micro-strip (only when jobs are starred) */}
      {shortlisted.size > 0 && (
        <div className="shrink-0 flex items-center gap-2 px-3 py-1 border-b t-tag"
          style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-brand-soft)' }}>
          <Star className="w-3 h-3" style={{ color: 'var(--ds-brand)' }} />
          <span style={{ color: 'var(--ds-brand)' }}>
            <strong>{shortlisted.size}</strong> shortlisted
          </span>
          <button type="button" onClick={() => onShortlistedChange(new Set())}
            className="ds-btn ds-btn-ghost ds-btn-sm ml-auto">
            Clear
          </button>
        </div>
      )}

      {/* ── Job content area ── */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col relative">
        {isLoading && jobs.length === 0 ? (
          <div className="flex-1 min-h-[12rem]" aria-hidden="true" />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
            <Briefcase className="w-10 h-10 t-muted" />
            <p className="t-body">No jobs found.</p>
            <p className="t-meta t-muted">Try a different search or remove filters.</p>
            {activeFilterCount > 0 && (
              <button type="button" onClick={clearFilters}
                className="t-link t-meta underline mt-1" style={{ color: 'var(--ds-brand)' }}>
                Clear filters
              </button>
            )}
          </div>
        ) : viewMode === 'list' ? (
          /* ── Table view — Untitled UI style ── */
          (() => {
            type SortCol = 'title' | 'score' | 'job_type' | 'location' | 'source' | 'posted' | '';
            type SortDir = 'asc' | 'desc';

            /* inline sort state — lifted into a tiny wrapper so hooks are at component top-level */
            const TableView = () => {
              const [sortCol, setSortCol] = React.useState<SortCol>('posted');
              const [sortDir, setSortDir] = React.useState<SortDir>('desc');

              const toggleSort = (col: SortCol) => {
                if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                // New column: title sorts asc by default, everything else desc
                else { setSortCol(col); setSortDir(col === 'title' ? 'asc' : 'desc'); }
              };

              const sortedRows = React.useMemo(() => {
                return [...paginated].sort((a, b) => {
                  let av: string | number = '';
                  let bv: string | number = '';
                  if (sortCol === 'title')    { av = a.title ?? ''; bv = b.title ?? ''; }
                  if (sortCol === 'score')    { av = recommendations.get(a.id)?.score ?? 0; bv = recommendations.get(b.id)?.score ?? 0; }
                  if (sortCol === 'job_type') { av = a.job_type ?? ''; bv = b.job_type ?? ''; }
                  if (sortCol === 'location') { av = a.location ?? ''; bv = b.location ?? ''; }
                  if (sortCol === 'source')   { av = (a as Job & { source_channel?: string }).source_channel ?? ''; bv = (b as Job & { source_channel?: string }).source_channel ?? ''; }
                  if (sortCol === 'posted')   { av = a.posted_at ? new Date(a.posted_at).getTime() : 0; bv = b.posted_at ? new Date(b.posted_at).getTime() : 0; }
                  if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
                  return sortDir === 'asc'
                    ? String(av).localeCompare(String(bv))
                    : String(bv).localeCompare(String(av));
                });
              }, [sortCol, sortDir]);

              /* Sortable column header — matches Untitled UI Table.Head style */
              const SortHead = ({
                col, label, tooltip, minWidth, className = '',
              }: { col: SortCol; label: string; tooltip?: string; minWidth?: number; className?: string }) => {
                const active = sortCol === col;
                return (
                  <th
                    className={`px-4 py-3 text-left whitespace-nowrap cursor-pointer select-none group/th ${className}`}
                    style={{ minWidth, color: active ? 'var(--ds-text-body)' : 'var(--ds-text-muted)' }}
                    onClick={() => toggleSort(col)}
                  >
                    <div className="flex items-center gap-1">
                      <span className="t-tag font-semibold">{label}</span>
                      {tooltip && (
                        <span title={tooltip}
                          className="w-3.5 h-3.5 rounded-full border t-tag font-bold flex items-center justify-center shrink-0"
                          style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-muted)' }}>
                          ?
                        </span>
                      )}
                      <span className={`t-tag ml-0.5 transition-opacity ${active ? 'opacity-100' : 'opacity-0 group-hover/th:opacity-40'}`}
                        style={{ color: 'var(--ds-text-muted)' }}>
                        {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </div>
                  </th>
                );
              };

              return (
                <div className="flex-1 min-h-0 overflow-auto mx-3 my-2 rounded-xl border"
                  style={{ borderColor: 'var(--ds-border)', boxShadow: 'var(--ds-shadow-card)' }}>
                  <table className="w-full border-collapse" style={{ minWidth: 1160 }}>
                    <thead>
                      <tr className="sticky top-0 z-10"
                        style={{ background: 'var(--ds-bg-tinted)', borderBottom: `1px solid var(--ds-border)` }}>

                        {/* Select-all checkbox */}
                        <th className="w-10 px-4 py-3">
                          <input
                            type="checkbox"
                            className="w-3.5 h-3.5 rounded accent-[var(--ds-brand)] cursor-pointer"
                            onChange={(e) => {
                              if (e.target.checked) setSelectedJobIds(new Set(paginated.map((j) => j.id)));
                              else setSelectedJobIds(new Set());
                            }}
                            checked={paginated.length > 0 && paginated.every((j) => selectedJobIds.has(j.id))}
                          />
                        </th>

                        <SortHead col="title"    label="Company" minWidth={150} />
                        <SortHead col="title"    label="Job"     minWidth={180} />
                        <SortHead col="score"    label="Match %"  tooltip="AI-computed match score based on your profile" minWidth={95} />
                        <SortHead col="source"   label="Source"   minWidth={100} />
                        <SortHead col="job_type" label="Role"     minWidth={100} />
                        <SortHead col="location" label="Location" minWidth={130} className="md:hidden xl:table-cell" />
                        <th className="px-4 py-3 text-left t-tag font-semibold whitespace-nowrap"
                          style={{ minWidth: 190 }}>
                          Skills
                        </th>
                        <SortHead col="posted" label="Posted" minWidth={100} />
                        <th className="w-24 px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((job, idx) => {
                        const rec = recommendations.get(job.id);
                        return (
                          <JobListRow
                            key={job.id}
                            job={job}
                            rec={rec}
                            blindMode={blindMode}
                            isShortlisted={shortlisted.has(job.id)}
                            isSelected={selectedJobIds.has(job.id)}
                            onSelect={() => toggleSelect(job.id)}
                            onShortlist={() => toggleShortlist(job)}
                            onClick={() => { setSelectedJob(job); onJobOpen?.(job.id); }}
                            isEven={idx % 2 === 0}
                            inCollection={jobInAnyCollection(job.id)}
                            collections={collections}
                            appliedInfo={lookupApplied(job.id, job.title, job.company)}
                            onAddToCollection={(cId) => handleAddToCollection(cId, job.id)}
                            onCreateCollection={(name) => handleCreateCollection(name, job.id)}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            };
            return <TableView key="table-view" />;
          })()
        ) : (
          /* ── Grid view — same JobCard as Jobs for you tab ── */
          <div
            className="agent-jobs-grid-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 py-2 sm:px-5 sm:py-4"
            style={{ scrollbarWidth: 'thin', width: '100%', minWidth: 0 }}
          >
            <div
              className="grid gap-4 w-full min-w-0 ds-agent-jobs-grid"
            >
              {paginated.map((job) => {
                const appliedInfo = lookupApplied(job.id, job.title, job.company);
                return (
                <JobCard
                  key={`${job.id}-${i18n.language}`}
                  job={job}
                  isSaved={savedLocal.has(job.id) || jobInAnyCollection(job.id)}
                  onToggleSave={toggleSave}
                  onApply={handleJobCardApply}
                  onView={handleJobCardClick}
                  onCardClick={handleJobCardClick}
                  statusBadge={
                    appliedInfo
                      ? <JobWorkflowStatusBadge info={appliedInfo} />
                      : (
                        <span
                          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap shrink-0"
                          style={{ background: 'rgba(148,163,184,0.15)', color: '#64748b', border: '1px solid rgba(148,163,184,0.25)' }}
                          title="Resume not optimized for this role yet"
                        >
                          Not optimized
                        </span>
                      )
                  }
                />
              );})}
            </div>
          </div>
        )}

        {/* ── Bulk selection action bar (table view only) ── */}
        {viewMode === 'list' && selectedJobIds.size > 0 && (
          <div
            className="shrink-0 flex items-center gap-2 px-4 py-2.5 border-t z-20"
            style={{
              borderColor: 'var(--ds-brand)',
              background: 'var(--ds-brand-soft)',
            }}
          >
            <div className="flex items-center gap-1.5 t-meta font-semibold" style={{ color: 'var(--ds-brand)' }}>
              <Check className="w-3.5 h-3.5" />
              {selectedJobIds.size} selected
            </div>

            {/* Add selected to collection */}
            <div className="relative" ref={bulkCollDropRef}>
              <button
                type="button"
                onClick={() => setBulkCollDropOpen((v) => !v)}
                className="ds-btn ds-btn-primary ds-btn-sm"
              >
                <FolderOpen className="w-3.5 h-3.5" />
                Add to collection
                <ChevronDown className="w-3 h-3 opacity-70" />
              </button>
              {bulkCollDropOpen && (
                <div className="ds-card-pop absolute bottom-full mb-1 left-0 z-50 py-1 w-[220px]">
                  <p className="px-3 py-1.5 t-label">
                    Save {selectedJobIds.size} job{selectedJobIds.size > 1 ? 's' : ''} to
                  </p>
                  {collections.length === 0 && (
                    <p className="px-3 py-2 t-tag t-muted italic">No collections yet — create one below.</p>
                  )}
                  {collections.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        [...selectedJobIds].forEach((jid) => {
                          if (!c.jobIds.includes(jid)) handleAddToCollection(c.id, jid);
                        });
                        setBulkCollDropOpen(false);
                        clearSelection();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 t-meta hover:bg-[var(--ds-bg-tinted)] text-left transition-colors"
                    >
                      <FolderOpen className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--ds-brand)' }} />
                      <span className="flex-1 truncate">{c.name}</span>
                      <span className="t-tag t-muted">{c.jobIds.length}</span>
                    </button>
                  ))}
                  <div className="border-t mx-2 my-1" style={{ borderColor: 'var(--ds-border)' }} />
                  <div className="px-2 py-1 flex gap-1">
                    <input
                      autoFocus={false}
                      placeholder="+ New collection…"
                      className="ds-input flex-1 min-w-0"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.target as HTMLInputElement).value.trim()) {
                          const name = (e.target as HTMLInputElement).value.trim();
                          const newColl: Collection = { id: `coll_${Date.now()}`, name, jobIds: [...selectedJobIds], createdAt: new Date().toISOString() };
                          onCollectionsChange([...collections, newColl]);
                          setBulkCollDropOpen(false);
                          clearSelection();
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={clearSelection}
              className="ds-btn ds-btn-ghost ds-btn-sm ml-auto"
            >
              Clear
            </button>
          </div>
        )}

        {/* ── Pagination controls ── */}
        {!isLoading && filtered.length > PAGE_SIZE && (
          <div className="agent-jobs-pagination shrink-0 flex items-center justify-between gap-2 px-2 py-2 border-t sm:px-4 sm:py-2.5"
            style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)' }}>
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="ds-btn ds-btn-secondary ds-btn-sm min-h-[40px] shrink-0 disabled:opacity-30"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Previous</span>
            </button>

            <div className="agent-jobs-pagination__pages hidden min-w-0 items-center justify-center gap-1 sm:flex">
              {/* Page number pills */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((item, i) =>
                  item === '...'
                    ? <span key={`ellipsis-${i}`} className="px-1 t-tag t-muted">…</span>
                    : (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setCurrentPage(item as number)}
                        className="w-7 h-7 rounded-md t-meta font-medium transition-all"
                        style={{
                          background: currentPage === item ? 'var(--ds-brand)' : 'transparent',
                          color:      currentPage === item ? '#fff' : 'var(--ds-text-body)',
                        }}
                      >
                        {item}
                      </button>
                    )
                )
              }
            </div>

            <span className="agent-jobs-pagination__compact shrink-0 tabular-nums t-tag font-medium sm:hidden">
              {currentPage} / {totalPages}
            </span>

            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="ds-btn ds-btn-secondary ds-btn-sm min-h-[40px] shrink-0 disabled:opacity-30"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Loading state for empty job list is handled by RightPanelProgress overlay */}

      {isLoading && jobs.length > 0 && (
        <div
          className="absolute top-0 left-0 right-0 z-40 h-0.5 overflow-hidden"
          style={{ background: 'var(--ds-border)' }}
          role="status"
          aria-label="Updating match scores"
        >
          <div
            className="h-full animate-pulse"
            style={{ width: '40%', background: 'var(--ds-brand)' }}
          />
        </div>
      )}

      {/* ── Job detail drawer ── */}
      <JobDetailDrawer
        job={selectedJob}
        onClose={() => setSelectedJob(null)}
        statusBadge={
          selectedJob
            ? (() => {
                const info = lookupApplied(selectedJob.id, selectedJob.title, selectedJob.company);
                return info ? <JobWorkflowStatusBadge info={info} size="md" /> : null;
              })()
            : null
        }
        onApply={() => {
          if (onAgentOptimize && selectedJob) {
            const raw = jobs.find(j => j.id === selectedJob.id);
            if (raw) { setSelectedJob(null); onAgentOptimize(raw); return; }
          }
          window.history.pushState({}, '', '/optimize');
          window.dispatchEvent(new PopStateEvent('popstate'));
        }}
        onView={(j) => window.open(j.url, '_blank')}
        onToggleSave={toggleSave}
        isSaved={selectedJob ? savedLocal.has(selectedJob.id) : false}
        allJobs={filtered}
        onSelectSimilar={(j) => { setSelectedJob(j); if (j) onJobOpen?.(j.id); }}
      />
    </div>
  );
};

// ── FilterChip already defined above (used inline), exported for others ─

// ── JobListRow ───────────────────────────────────────────────────────

// Cycling badge colors for skill chips — mirrors the Teams column palette
const SKILL_COLORS = [
  { bg: 'rgba(108,71,255,0.10)', text: '#6C47FF' },   // purple
  { bg: 'rgba(59,130,246,0.10)', text: '#3b82f6' },   // blue
  { bg: 'rgba(16,185,129,0.10)', text: '#10b981' },   // green
  { bg: 'rgba(245,158,11,0.10)', text: '#d97706' },   // amber
  { bg: 'rgba(239,68,68,0.10)',  text: '#ef4444' },   // red
];

interface JobListRowProps {
  job:           Job;
  rec?:          RecommendationResult;
  isShortlisted: boolean;
  isSelected:    boolean;
  isEven:        boolean;
  inCollection:  boolean;
  collections:   Collection[];
  blindMode:     boolean;
  appliedInfo:   AppliedJobInfo | null;
  onSelect:      () => void;
  onShortlist:   () => void;
  onClick:       () => void;
  onAddToCollection: (collId: string) => void;
  onCreateCollection: (name: string) => void;
}

function cleanSkills(raw: string[] | undefined): string[] {
  if (!raw) return [];
  return raw.filter(
    (s) => s && s.length < 40 && !s.toLowerCase().includes('extraction') && !s.toLowerCase().includes('unavailable'),
  );
}

const JobListRow: React.FC<JobListRowProps> = ({
  job, rec, isShortlisted, isSelected, isEven, inCollection, collections, blindMode, appliedInfo,
  onSelect, onShortlist, onClick, onAddToCollection, onCreateCollection,
}) => {
  const { title: displayTitle } = useJobContentTranslation(job, { translateDescription: false });
  const score          = rec?.score ?? 0;
  const scoreColor     = rec ? (score >= 70 ? '#10b981' : score >= 50 ? '#3b82f6' : score >= 35 ? '#f59e0b' : '#ef4444') : null;
  const scoreDotColor  = scoreColor;
  const topSkills      = cleanSkills(job.skills).slice(0, 3);
  const extraSkills    = cleanSkills(job.skills).length - 3;
  const displayCompany = blindMode ? _blindLabel(job.id) : (job.company ?? '—');
  const displayLoc     = blindMode ? '—' : (job.location || '—');
  const srcCh          = (job as Job & { source_channel?: string }).source_channel;
  const srcLabel       = srcCh ? ({ peerlist: 'Peerlist', linkedin: 'LinkedIn', naukri: 'Naukri' }[srcCh] ?? srcCh) : null;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const rowBg = isEven ? 'var(--ds-bg-tinted)' : 'var(--ds-bg-card)';

  return (
    <tr
      onClick={onClick}
      className="group cursor-pointer transition-colors duration-100"
      style={{ background: isSelected ? 'var(--ds-brand-soft)' : rowBg, borderBottom: '1px solid var(--ds-border)' }}
      onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'var(--ds-brand-soft)'; }}
      onMouseLeave={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = rowBg; }}
    >
      {/* ── Checkbox ── */}
      <td className="w-10 px-4 py-0" onClick={(e) => { e.stopPropagation(); onSelect(); }}>
        <input
          type="checkbox"
            className="w-3.5 h-3.5 rounded accent-[var(--ds-brand)] cursor-pointer"
          checked={isSelected}
          onChange={onSelect}
          onClick={(e) => e.stopPropagation()}
        />
      </td>

      {/* ── Company column — circular avatar + name only ── */}
      <td className="px-4 py-3" style={{ maxWidth: 180 }}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold shrink-0"
            style={{ background: 'var(--ds-brand-soft)', color: 'var(--ds-brand)' }}>
            {(displayCompany.trim()[0] ?? '?').toUpperCase()}
          </div>
          <p className="t-meta font-medium leading-snug truncate min-w-0">
            {displayCompany}
          </p>
        </div>
      </td>

      {/* ── Job title column ── */}
      <td className="px-4 py-3" style={{ maxWidth: 220 }}>
        <div className="flex items-center gap-2">
          <p className="t-card-title leading-snug truncate flex-1">
            {displayTitle}
          </p>
          {appliedInfo ? (
            <JobWorkflowStatusBadge info={appliedInfo} />
          ) : (
            <span
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap shrink-0"
              style={{ background: 'rgba(148,163,184,0.15)', color: '#64748b', border: '1px solid rgba(148,163,184,0.25)' }}
              title="Resume not optimized for this role yet"
            >
              Not optimized
            </span>
          )}
        </div>
        {job.experience_level && (
          <p className="t-tag t-muted mt-0.5 truncate">
            {job.experience_level}
          </p>
        )}
      </td>

      {/* ── Match % — badge with colored dot ── */}
      <td className="px-4 py-3 whitespace-nowrap">
        {rec && scoreColor ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full t-tag font-semibold border"
            style={{
              background: `${scoreColor}12`,
              borderColor: `${scoreColor}30`,
              color: scoreColor,
            }}>
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: scoreDotColor ?? undefined }} />
            {score.toFixed(0)}%
          </span>
        ) : (
          <span className="t-tag t-muted">—</span>
        )}
      </td>

      {/* ── Source channel ── */}
      <td className="px-4 py-3 whitespace-nowrap">
        {!blindMode && srcLabel ? (
          <span className="ds-chip ds-chip-brand t-tag">
            {srcLabel}
          </span>
        ) : (
          <span className="t-tag t-muted">—</span>
        )}
      </td>

      {/* ── Type / Role — remote badge OR job_type pill ── */}
      <td className="px-4 py-3 whitespace-nowrap">
        {job.remote ? (
          <span className="ds-chip ds-chip-ok t-tag">
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--ds-ok)' }} />
            Remote
          </span>
        ) : job.job_type ? (
          <span className="ds-chip t-tag">
            {job.job_type}
          </span>
        ) : (
          <span className="t-tag t-muted">—</span>
        )}
      </td>

      {/* ── Location ── */}
      <td className="px-4 py-3 whitespace-nowrap">
        {!blindMode && job.location ? (
          <span className="flex items-center gap-1 t-meta">
            <MapPin className="w-3 h-3 shrink-0" style={{ color: 'var(--ds-danger)' }} />
            {displayLoc}
          </span>
        ) : (
          <span className="t-meta t-muted">—</span>
        )}
      </td>

      {/* ── Required Skills — colored team-style badges ── */}
      <td className="px-4 py-3">
        {topSkills.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {topSkills.map((s, i) => {
              const c = SKILL_COLORS[i % SKILL_COLORS.length];
              return (
                <span key={`${s}-${i}`} className="px-2 py-0.5 rounded-full t-tag font-semibold"
                  style={{ background: c.bg, color: c.text }}>
                  {s}
                </span>
              );
            })}
            {extraSkills > 0 && (
              <span className="ds-chip t-muted">
                +{extraSkills}
              </span>
            )}
          </div>
        ) : <span className="t-tag t-muted">—</span>}
      </td>

      {/* ── Posted date ── */}
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="flex items-center gap-1 t-tag t-muted">
          <Clock className="w-3 h-3 shrink-0" />
          {agentFreshnessLabel(job.posted_at) || '—'}
        </span>
      </td>

      {/* ── Actions — icon buttons ── */}
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {/* Shortlist star */}
          <button type="button" title={isShortlisted ? 'Remove shortlist' : 'Shortlist'}
            onClick={(e) => { e.stopPropagation(); onShortlist(); }}
            className="ds-btn ds-btn-ghost w-7 h-7 p-0"
            style={{ color: '#f59e0b', opacity: isShortlisted ? 1 : 0.5 }}>
            <Star className={`w-4 h-4 ${isShortlisted ? 'fill-current' : ''}`} />
          </button>

          {/* Bookmark / collection */}
          <div className="relative" ref={menuRef}>
            <button type="button" title="Save to collection"
              onClick={() => setMenuOpen((v) => !v)}
              className="ds-btn ds-btn-ghost w-7 h-7 p-0"
              style={{ color: inCollection ? 'var(--ds-brand)' : 'var(--ds-text-muted)' }}>
              {inCollection ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            </button>
            {menuOpen && (
              <CollectionDropdown
                jobId={job.id}
                collections={collections}
                onAdd={(cId) => { onAddToCollection(cId); setMenuOpen(false); }}
                onCreate={(name) => { onCreateCollection(name); setMenuOpen(false); }}
                onClose={() => setMenuOpen(false)}
              />
            )}
          </div>

          {/* View → */}
          <button type="button" title="View job"
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            className="ds-btn ds-btn-ghost w-7 h-7 p-0"
            style={{ color: 'var(--ds-brand)', background: 'var(--ds-brand-soft)' }}>
            <ChevronRight className="w-4 h-4" />
          </button>
          <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-70 transition-opacity"
            style={{ color: 'var(--ds-brand)' }} />
        </div>
      </td>
    </tr>
  );
};

export default AgentJobsPanel;
